import { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise = null;

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.turnstile), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return turnstileScriptPromise;
};

const TurnstileWidget = ({ siteKey, onTokenChange, resetSignal = 0, className = '' }) => {
  const observerRef = useRef(null);
  const mountRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (!siteKey || !mountRef.current) return undefined;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '280px 0px',
      }
    );

    observerRef.current.observe(mountRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !shouldMount || !mountRef.current || widgetIdRef.current !== null) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !mountRef.current || widgetIdRef.current !== null) {
          return;
        }

        widgetIdRef.current = turnstile.render(mountRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenChange(token),
          'expired-callback': () => onTokenChange(''),
          'error-callback': () => onTokenChange(''),
          theme: 'auto',
        });
      })
      .catch(() => {
        onTokenChange('');
      });

    return () => {
      cancelled = true;
    };
  }, [onTokenChange, shouldMount, siteKey]);

  useEffect(() => {
    if (!window.turnstile || widgetIdRef.current === null) return;
    window.turnstile.reset(widgetIdRef.current);
  }, [resetSignal]);

  if (!siteKey) return null;

  return (
    <div
      className={`min-h-[92px] rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/5 ${className}`}
    >
      <div ref={mountRef} className="min-h-[65px]" />
    </div>
  );
};

export default TurnstileWidget;
